import React, { useState, useEffect, useRef, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import './components/SnowflakeChart/SnowflakeChart.css';
import LandingPage from './LandingPage';
import { AuthProvider, useAuth } from './AuthContext';
import { AuthService } from './AuthService';
import ComparisonAccessSheet from './components/ComparisonAccessSheet/ComparisonAccessSheet';
import {
  selectionBlockReason,
  compareLimitForUser,
  readPendingComparisonAdd,
  storePendingComparisonAdd,
  clearPendingComparisonAdd,
} from './comparisonAccess';
import { PREMIUM_COMPARE_LIMIT } from './constants/pricingFeatures';
import Header from './components/Header/Header';
import SEOHead from './components/SEOHead/SEOHead';
import DomainRedirectManager from './components/DomainRedirectManager/DomainRedirectManager';
import NotFound from './components/NotFound/NotFound';
import CookieConsent from './components/CookieConsent/CookieConsent';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import RouteWithTransition, { RouteErrorBoundary } from './components/RouteWithTransition';
import GoogleMapsGate from './components/GoogleMapsLoadScript/GoogleMapsGate';
import { GoogleMapsProvider } from './components/GoogleMapsLoadScript/GoogleMapsProvider';
import { captureAcquisitionContext, initGA, logPageView } from './utils/analytics';
import { hasAnalyticsConsent } from './utils/cookieConsent';
import Footer from './components/Footer/Footer';
import './styles/site-overrides.css';
import './styles/route-overrides.css';
import './styles/modal-and-polish.css';

const ComparisonPage = lazy(() => import('./ComparisonPage'));
const MapPage = lazy(() => import('./MapPage'));
const PaymentSuccess = lazy(() => import('./PaymentSuccess'));
const PaymentCancel = lazy(() => import('./PaymentCancel'));
const LoginPage = lazy(() => import('./LoginPage'));
const RegisterPage = lazy(() => import('./RegisterPage'));
const ResetPasswordRequestPage = lazy(() => import('./ResetPasswordRequestPage'));
const ResetPasswordConfirmPage = lazy(() => import('./ResetPasswordConfirmPage'));
const ProfilePage = lazy(() => import('./ProfilePage'));
const InstitutionDetailPage = lazy(() => import('./InstitutionDetailPage'));
const InstitutionListPage = lazy(() => import('./InstitutionListPage'));
const DistrictInstitutionListPage = lazy(() => import('./DistrictInstitutionListPage'));
const AboutPage = lazy(() => import('./AboutPage'));
const PrivacyPolicyPage = lazy(() => import('./PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./TermsOfServicePage'));

const DEBUG = String(process.env.DEBUG || '').toLowerCase() === 'true';

function AppContent() {
  const location = useLocation();
  const { user, isLoading } = useAuth();
  const [paymentEnabled, setPaymentEnabled] = useState(null);
  const [accessSheet, setAccessSheet] = useState(null);
  const loadStoredSelection = () => {
    try {
      const stored = localStorage.getItem('selectedInstitutions');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.every((inst) => inst.id && inst.name)) {
          return parsed;
        }
      }
    } catch (error) {
      if (DEBUG) {
        console.warn('Failed to load selection from localStorage:', error);
      }
    }
    return [];
  };

  const [selectedInstitutions, setSelectedInstitutions] = useState(loadStoredSelection);
  const selectedInstitutionsRef = useRef(selectedInstitutions);
  selectedInstitutionsRef.current = selectedInstitutions;
  const navigate = useNavigate();

  useEffect(() => {
    try {
      localStorage.setItem('selectedInstitutions', JSON.stringify(selectedInstitutions));
    } catch (error) {
      if (DEBUG) {
        console.warn('Failed to save selection to localStorage:', error);
      }
    }
  }, [selectedInstitutions]);

  useEffect(() => {
    AuthService.getPaymentConfig()
      .then((config) => setPaymentEnabled(Boolean(config?.payment_enabled)))
      .catch(() => setPaymentEnabled(false));
  }, []);

  const handleSelectionChange = (newSelection) => {
    const current = selectedInstitutionsRef.current;
    const next = Array.isArray(newSelection) ? newSelection : [];
    const added = next.filter((inst) => !current.some((item) => item.id === inst.id));
    if (added.length && (paymentEnabled === null || isLoading)) {
      return { blocked: true, reason: 'loading' };
    }
    const reason = selectionBlockReason(user, current.length, added.length, paymentEnabled);
    if (reason) {
      if (reason === 'limit') {
        storePendingComparisonAdd(added[0]);
      }
      setAccessSheet({ reason, institution: added[0] });
      return { blocked: true, reason };
    }
    selectedInstitutionsRef.current = next;
    setSelectedInstitutions(next);
    return { blocked: false };
  };

  const handleAccessAuthenticated = (authedUser) => {
    const institution = accessSheet?.institution;
    setAccessSheet(null);
    if (!institution?.id) return;
    const current = selectedInstitutionsRef.current;
    const limit = compareLimitForUser(authedUser);
    if (current.some((item) => item.id === institution.id)) return;
    if (current.length >= limit) {
      storePendingComparisonAdd(institution);
      setAccessSheet({ reason: 'limit', institution });
      return;
    }
    clearPendingComparisonAdd();
    setSelectedInstitutions([...current, institution]);
  };

  useEffect(() => {
    if (isLoading || paymentEnabled === null || !paymentEnabled || !user) return;
    const pending = readPendingComparisonAdd();
    if (!pending?.id) return;
    const current = selectedInstitutionsRef.current;
    if (current.some((item) => item.id === pending.id)) {
      clearPendingComparisonAdd();
      return;
    }
    const limit = compareLimitForUser(user);
    if (current.length >= limit) {
      setAccessSheet({ reason: 'limit', institution: pending });
      return;
    }
    clearPendingComparisonAdd();
    const next = [...current, pending];
    setSelectedInstitutions(
      AuthService.isPremiumActiveUser(user) ? next.slice(0, PREMIUM_COMPARE_LIMIT) : next
    );
  }, [user, isLoading, paymentEnabled]);

  useEffect(() => {
    if (isLoading || paymentEnabled !== true || user) return;
    if (selectedInstitutionsRef.current.length) {
      setSelectedInstitutions([]);
    }
  }, [user, isLoading, paymentEnabled]);

  const handleCompare = () => {
    const selection = selectedInstitutionsRef.current;
    if (selection.length < 2) return;
    const reason = selectionBlockReason(user, 0, selection.length, paymentEnabled !== false);
    if (reason) {
      setAccessSheet({ reason, institution: selection[0] || null });
      return;
    }
    navigate('/compare');
  };

  useEffect(() => {
    captureAcquisitionContext();
    if (hasAnalyticsConsent()) {
      initGA();
    }
  }, []);

  useEffect(() => {
    captureAcquisitionContext();
    logPageView(location.pathname);
  }, [location]);

  return (
    <div className="App">
      <DomainRedirectManager />
      <SEOHead />
      <Header />

      <main className="app-content">
        <RouteErrorBoundary resetKey={location.pathname}>
          <Routes location={location}>
            <Route
              path="/"
              element={
                <RouteWithTransition>
                  <LandingPage />
                </RouteWithTransition>
              }
            />
            <Route
              path="/map"
              element={
                <ProtectedRoute requirePremium feature="map">
                  <GoogleMapsGate loadingMessage="Indlæser kort...">
                    <RouteWithTransition>
                      <MapPage
                        selectedInstitutions={selectedInstitutions}
                        onSelectionChange={handleSelectionChange}
                        onCompare={handleCompare}
                      />
                    </RouteWithTransition>
                  </GoogleMapsGate>
                </ProtectedRoute>
              }
            />
              <Route
                path="/compare"
                element={
                  <RouteWithTransition>
                    <ComparisonPage selectedInstitutions={selectedInstitutions} />
                  </RouteWithTransition>
                }
              />
              <Route
                path="/institution-list"
                element={
                  <RouteWithTransition>
                    <InstitutionListPage
                      selectedInstitutions={selectedInstitutions}
                      onSelectionChange={handleSelectionChange}
                      onCompare={handleCompare}
                    />
                  </RouteWithTransition>
                }
              />
              <Route
                path="/institutioner/bydel/:bydelSlug"
                element={
                  <RouteWithTransition>
                    <DistrictInstitutionListPage
                      selectedInstitutions={selectedInstitutions}
                      onSelectionChange={handleSelectionChange}
                      onCompare={handleCompare}
                    />
                  </RouteWithTransition>
                }
              />
              <Route
                path="/payment-success"
                element={
                  <RouteWithTransition>
                    <PaymentSuccess />
                  </RouteWithTransition>
                }
              />
              <Route
                path="/payment-cancel"
                element={
                  <RouteWithTransition>
                    <PaymentCancel />
                  </RouteWithTransition>
                }
              />
              <Route
                path="/login"
                element={
                  <RouteWithTransition>
                    <LoginPage />
                  </RouteWithTransition>
                }
              />
              <Route
                path="/register"
                element={
                  <RouteWithTransition>
                    <RegisterPage />
                  </RouteWithTransition>
                }
              />
              <Route
                path="/reset-password"
                element={
                  <RouteWithTransition>
                    <ResetPasswordRequestPage />
                  </RouteWithTransition>
                }
              />
              <Route
                path="/reset-password/:uid/:token"
                element={
                  <RouteWithTransition>
                    <ResetPasswordConfirmPage />
                  </RouteWithTransition>
                }
              />
              <Route path="/mine-lister" element={<Navigate to="/" replace />} />
              <Route path="/anbefalinger" element={<Navigate to="/" replace />} />
              <Route
                path="/profile"
                element={
                  <RouteWithTransition>
                    <ProfilePage />
                  </RouteWithTransition>
                }
              />
              <Route
                path="/institution/:slug"
                element={
                  <RouteWithTransition>
                    <InstitutionDetailPage
                      selectedInstitutions={selectedInstitutions}
                      onSelectionChange={handleSelectionChange}
                      onCompare={handleCompare}
                    />
                  </RouteWithTransition>
                }
              />
              <Route
                path="/om"
                element={
                  <RouteWithTransition>
                    <AboutPage />
                  </RouteWithTransition>
                }
              />
              <Route path="/privatliv" element={<RouteWithTransition><PrivacyPolicyPage /></RouteWithTransition>} />
              <Route path="/vilkar" element={<RouteWithTransition><TermsOfServicePage /></RouteWithTransition>} />
              <Route
                path="/404"
                element={
                  <RouteWithTransition>
                    <NotFound />
                  </RouteWithTransition>
                }
              />
              <Route
                path="*"
                element={
                  <RouteWithTransition>
                    <NotFound />
                  </RouteWithTransition>
                }
              />
            </Routes>
        </RouteErrorBoundary>

          {location.pathname === '/' && <Footer />}
        </main>

        <CookieConsent />
        <ComparisonAccessSheet
          sheet={accessSheet}
          onClose={() => setAccessSheet(null)}
          onAuthenticated={handleAccessAuthenticated}
        />
      </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <GoogleMapsProvider>
          <AppContent />
        </GoogleMapsProvider>
      </Router>
    </AuthProvider>
  );
}

export default App;
